/**
 * Currency helpers.
 *
 * Maps ISO 4217 codes → symbol used across the app. Falls back to the
 * code itself when a symbol isn't known (e.g. "KES 1,234").
 */
import { useHotelContext } from "@/contexts/HotelContext";


const SYMBOLS = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CNY: "¥",
  AED: "د.إ",
  AUD: "A$",
  CAD: "C$",
  SGD: "S$",
  HKD: "HK$",
  CHF: "CHF ",
  NZD: "NZ$",
  ZAR: "R",
  BRL: "R$",
  SEK: "kr ",
  NOK: "kr ",
  DKK: "kr ",
  THB: "฿",
  MYR: "RM",
  IDR: "Rp",
  KRW: "₩",
  RUB: "₽",
  TRY: "₺",
  MXN: "$",
  PHP: "₱",
  VND: "₫",
  LKR: "Rs ",
  NPR: "Rs ",
  PKR: "Rs ",
  BDT: "৳",
};

/** Return the display symbol for an ISO code (falls back to the code). */
export function currencySymbol(code) {
  if (!code) return "$";
  const c = String(code).toUpperCase().trim();
  return SYMBOLS[c] || `${c} `;
}

/** Format an amount with the given currency's symbol. */
export function fmtMoney(amount, code, decimals = 2) {
  const n = Number(amount || 0);
  const sym = currencySymbol(code);
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/**
 * React hook — returns a money formatter bound to the active hotel's currency.
 * Usage: const money = useCurrency(); money(1234.5) -> "₹1,234.50"
 */
export function useCurrency() {
  const { ctx } = useHotelContext() || {};
  const code = ctx?.currency || "INR";
  const fmt = (amount, decimals = 2) => fmtMoney(amount, code, decimals);
  fmt.code = code;
  fmt.symbol = currencySymbol(code);
  return fmt;
}
